import type { OtcQuote } from "@/modules/dashboard/crypto-swaps/types/cryptoSwaps.types";

import type { CreateCardCopTopUpPayload } from "../types/cardCopTopUp.types";

export interface CopTopUpAttempt {
  quote: OtcQuote;
  payload: CreateCardCopTopUpPayload;
}

export interface CopTopUpFailure {
  unresolved: CopTopUpAttempt | null;
  messages: string[];
}

export type CopTopUpLookup = "not_placed" | "unknown";

export const COP_TOP_UP_NOT_FOUND_CODE = "cop_top_up_not_found";

// No answer, a 5xx, a timeout, the key still running or a throttle: the request may have bought.
const AMBIGUOUS_STATUSES: ReadonlySet<number> = new Set([408, 409, 429]);

export function freshCopTopUpAttempt(
  quote: OtcQuote | null,
  idempotencyKey: string | null,
  isExpired: boolean,
): CopTopUpAttempt | null {
  if (!quote || !idempotencyKey || isExpired) return null;
  return { quote, payload: { quote_id: quote.id, idempotency_key: idempotencyKey } };
}

// Only for the first send of a key: any other answer is about that send, and no earlier one exists.
export function settleFreshFailure(
  attempt: CopTopUpAttempt,
  status: number | null,
  messages: string[],
): CopTopUpFailure {
  if (status === null || status >= 500 || AMBIGUOUS_STATUSES.has(status)) {
    return { unresolved: attempt, messages: [] };
  }
  return { unresolved: null, messages };
}

// Once unresolved, only the lookup by key settles it: a guard (session, 2FA) answers before the key is read.
export function settleLookupFailure(status: number | null, code: string | null): CopTopUpLookup {
  return status === 404 && code === COP_TOP_UP_NOT_FOUND_CODE ? "not_placed" : "unknown";
}

// A 404 while following means the top-up was dropped as refused: the copy from the create is stale by then.
export function followedCopTopUp<T>(
  followed: { order: T | null; notFound: boolean },
  created: T | null,
): T | null {
  if (followed.notFound) return null;
  return followed.order ?? created;
}

type AttemptStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const STORAGE_KEY = "coloca.cardCopTopUp.unresolved";

const isAttempt = (value: unknown): value is CopTopUpAttempt => {
  const attempt = value as Partial<CopTopUpAttempt> | null;
  return (
    !!attempt &&
    typeof attempt.payload?.quote_id === "string" &&
    typeof attempt.payload?.idempotency_key === "string" &&
    typeof attempt.quote?.id === "string" &&
    typeof attempt.quote?.send?.amount === "string" &&
    typeof attempt.quote?.receive?.amount === "string"
  );
};

// Survives a reload: written before the request leaves, removed only once its outcome is known.
export function createUnresolvedStore(storage: AttemptStorage | null) {
  const memory = new Map<string, CopTopUpAttempt>();

  const readAll = (): Record<string, unknown> => {
    try {
      const raw = storage?.getItem(STORAGE_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  };

  const writeAll = (all: Record<string, unknown>) => {
    try {
      if (Object.keys(all).length) storage?.setItem(STORAGE_KEY, JSON.stringify(all));
      else storage?.removeItem(STORAGE_KEY);
    } catch {
      return;
    }
  };

  return {
    get(cardId: string): CopTopUpAttempt | null {
      const stored = readAll()[cardId];
      return isAttempt(stored) ? stored : (memory.get(cardId) ?? null);
    },
    set(cardId: string, attempt: CopTopUpAttempt | null): void {
      const all = readAll();
      if (attempt) {
        memory.set(cardId, attempt);
        all[cardId] = attempt;
      } else {
        memory.delete(cardId);
        delete all[cardId];
      }
      writeAll(all);
    },
  };
}

const browserStorage = (): AttemptStorage | null => {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
};

export const unresolvedCopTopUps = createUnresolvedStore(browserStorage());
